import type { Province, ReturningArmy, Training } from '../../types/province';
import type { ModifierValue } from '../../utils/economyFlow';
import { formatNumber, formatPerAcre, pct } from '../../utils/format';
import { RACE_UNIT_STATS, RACE_UNITS, getRaceUnitName } from '../../constants/game';
import { HONOUR_INCOME_MOD } from '../../constants/modifierLookup';

// SF-180 Decision 8: explicit fallback for the optional modifiedOffensePoints/modifiedDefensePoints
// DTO fields — a pre-deploy Redis-cached entry lacks them entirely. Never `?? 0`, which would
// resurrect the exact "Modified shows 0" bug this spec exists to fix for the stale-cache window.
function formatModified(value: number | undefined): string {
  return typeof value === 'number' ? formatNumber(value) : '—';
}

function formatModifiedPerAcre(value: number | undefined, acres: number): string {
  return typeof value === 'number' ? formatPerAcre(value, acres) : '—';
}

/**
 * SF-162: pure row-building logic for `TrainingOverviewTab.tsx`, extracted into its own
 * co-located module (Component 1e) — mirrors `sciences/investmentPlanner.ts`'s precedent (SF-161),
 * not `utils/economyFlow.ts`'s centralized location. Ports/restructures `TroopsTab.tsx` +
 * `MilitaryPowerTab.tsx` content (both deleted by this spec).
 */

// ─── Stat cards ─────────────────────────────────────────────────────────────

export interface StatCardData {
  key: string;
  label: string;
  value: string;
  sub: string;
}

/** Component 1a: 8 cards across two labeled rows — Generals/Soldiers dropped as standalone cards
 *  (their information relocates into Army Composition, 1b); SF-183 added "Defense at home" as an
 *  at-home-only sibling to Net Defense. */
export function buildStatCards(p: Province): { combatPower: StatCardData[]; status: StatCardData[] } {
  const acres = p.territory.totals.acres;
  const mil = p.military;
  const totalInTraining = mil.training.reduce((sum, t) => sum + t.unitCount, 0);

  const combatPower: StatCardData[] = [
    { key: 'offensePerAcre', label: 'Offense Per Acre', value: formatModifiedPerAcre(mil.modifiedOffensePoints, acres), sub: `${formatModified(mil.modifiedOffensePoints)} total pts` },
    { key: 'defensePerAcre', label: 'Defense Per Acre', value: formatModifiedPerAcre(mil.modifiedDefensePoints, acres), sub: `${formatModified(mil.modifiedDefensePoints)} total pts` },
    { key: 'omeDme', label: 'OME / DME', value: `${pct(mil.offensiveME)} / ${pct(mil.defensiveME)}`, sub: 'military efficiency' },
    // SF-180: sub-caption changed from "at home" — the new fields (like the retired net*AtHome
    // fields before them) are away-inclusive, not at-home-only (Decision 4).
    { key: 'netOffense', label: 'Net Offense', value: formatModified(mil.modifiedOffensePoints), sub: 'modified' },
    { key: 'netDefense', label: 'Net Defense', value: formatModified(mil.modifiedDefensePoints), sub: 'modified' },
    // SF-183 Component 8: genuinely at-home-only (unlike every field above, which stays
    // deliberately away-inclusive per SF-180 Decision 4) — the number live combat actually
    // resolves against once a province's army is away.
    { key: 'defenseAtHome', label: 'Defense at home', value: `${formatModified(mil.defenseAtHomePoints)} dpa`, sub: 'at-home only' },
  ];

  const status: StatCardData[] = [
    { key: 'wageRate', label: 'Wage Rate', value: `${(mil.wageRate * 100).toFixed(0)}%`, sub: 'military pay' },
    { key: 'inTraining', label: 'In Training', value: formatNumber(Math.round(totalInTraining)), sub: `${mil.training.length} batches` },
  ];

  return { combatPower, status };
}

// ─── Mobile stat rows (MetricRow-shaped, CLAUDE.md label-unit-value layout) ─
// `MetricRow` requires a bare numeric `value` and a separate `unit` string — unlike `StatCard`
// above, whose `value` is a free-form pre-formatted string. `buildStatCards`' `omeDme` (a
// composite "X% / Y%" string) and `wageRate` (a `%`-baked string) can't be reused verbatim for
// mobile without violating that convention, so this is a distinct builder rather than a shared
// shape — `omeDme` splits into two independent rows (Offense ME / Defense ME) here.

export interface MetricRowData {
  key: string;
  label: string;
  value: string | number;
  unit: string;
}

export function buildCompactStatRows(p: Province): { combatPower: MetricRowData[]; status: MetricRowData[] } {
  const acres = p.territory.totals.acres;
  const mil = p.military;
  const totalInTraining = mil.training.reduce((sum, t) => sum + t.unitCount, 0);

  const combatPower: MetricRowData[] = [
    { key: 'offensePerAcre', label: 'Offense Per Acre', value: formatModifiedPerAcre(mil.modifiedOffensePoints, acres), unit: '' },
    { key: 'defensePerAcre', label: 'Defense Per Acre', value: formatModifiedPerAcre(mil.modifiedDefensePoints, acres), unit: '' },
    { key: 'offensiveME', label: 'Offense ME', value: (mil.offensiveME * 100).toFixed(1), unit: '%' },
    { key: 'defensiveME', label: 'Defense ME', value: (mil.defensiveME * 100).toFixed(1), unit: '%' },
    { key: 'netOffense', label: 'Net Offense', value: formatModified(mil.modifiedOffensePoints), unit: '' },
    { key: 'netDefense', label: 'Net Defense', value: formatModified(mil.modifiedDefensePoints), unit: '' },
    // SF-183 Component 8: genuinely at-home-only — see buildStatCards' identical desktop entry.
    { key: 'defenseAtHome', label: 'Defense at home', value: formatModified(mil.defenseAtHomePoints), unit: 'dpa' },
  ];

  const status: MetricRowData[] = [
    { key: 'wageRate', label: 'Wage Rate', value: (mil.wageRate * 100).toFixed(0), unit: '%' },
    { key: 'inTraining', label: 'In Training', value: formatNumber(Math.round(totalInTraining)), unit: '' },
  ];

  return { combatPower, status };
}

// ─── Army Composition (Component 1b) ────────────────────────────────────────

export type ArmyUnitKind = 'soldier' | 'offenseSpecialist' | 'defenseSpecialist' | 'elite' | 'warHorses';

export interface ArmyUnitRow {
  kind: ArmyUnitKind;
  count: number;
}

const HOME_UNIT_ORDER: ArmyUnitKind[] = ['soldier', 'offenseSpecialist', 'defenseSpecialist', 'elite', 'warHorses'];
/** Defense specialists never deploy — excluded unconditionally from every away-army card. */
const AWAY_UNIT_ORDER: ArmyUnitKind[] = ['soldier', 'offenseSpecialist', 'elite', 'warHorses'];

/** Ordered, nonzero-filtered unit rows for one location — Home (`army === null`) or a specific
 *  away army. Inherits `TroopsTab.tsx`'s existing Thieves/Prisoners omission (Deferred Item 1). */
export function buildUnitRows(p: Province, army: ReturningArmy | null): ArmyUnitRow[] {
  const counts: Record<ArmyUnitKind, number> = army
    ? { soldier: army.soldiers, offenseSpecialist: army.offenseSpecialists, defenseSpecialist: 0, elite: army.elites, warHorses: army.warHorses }
    : {
        // SF-183 Component 8: at-home only (with the away-inclusive-sibling fallback) for the
        // types that travel in an Army — this Home entry rendered alongside one entry per away
        // army built from that same army's own unit counts, so an away-inclusive Home read
        // double-counted the identical soldiers on screen. defenseSpecialist never travels in an
        // Army, so away is always 0 for it — unchanged.
        soldier: p.population.soldiersAtHome ?? p.population.soldiers,
        offenseSpecialist: p.population.offenseSpecialistsAtHome ?? p.population.offenseSpecialists,
        defenseSpecialist: p.population.defenseSpecialists,
        elite: p.population.elitesAtHome ?? p.population.elites,
        warHorses: p.population.warHorsesAtHome ?? p.population.warHorses,
      };

  const order = army ? AWAY_UNIT_ORDER : HOME_UNIT_ORDER;
  return order.map((kind) => ({ kind, count: counts[kind] })).filter((row) => row.count > 0);
}

export interface ArmyCompositionEntry {
  key: string;
  isHome: boolean;
  generalNumber: number | null;
  /** Home only — `generals - returningArmies.length` (available / total). */
  generalsAvailable: number | null;
  generalsTotal: number | null;
  /** Away only. */
  ticksRemaining: number | null;
  returnTimeFraction: number | null;
  units: ArmyUnitRow[];
}

/** Location-first restructure (Home, then one entry per away army, ordered by general number) —
 *  a deliberate reversal of `TroopsTab.tsx`'s original unit-type-first table shape. */
export function buildArmyComposition(p: Province): ArmyCompositionEntry[] {
  const armies = p.military.returningArmies;

  const home: ArmyCompositionEntry = {
    key: 'home',
    isHome: true,
    generalNumber: null,
    generalsAvailable: p.population.generals - armies.length,
    generalsTotal: p.population.generals,
    ticksRemaining: null,
    returnTimeFraction: null,
    units: buildUnitRows(p, null),
  };

  const away: ArmyCompositionEntry[] = [...armies]
    .sort((a, b) => a.generalNumber - b.generalNumber)
    .map((army) => ({
      key: `away-${army.generalNumber}`,
      isHome: false,
      generalNumber: army.generalNumber,
      generalsAvailable: null,
      generalsTotal: null,
      ticksRemaining: army.ticksRemaining,
      returnTimeFraction: army.returnTimeFraction,
      units: buildUnitRows(p, army),
    }));

  return [home, ...away];
}

// ─── Offense/Defense breakdown (Component 1d) ───────────────────────────────
// Renamed out of the deleted `council/tabs/MilitaryPowerTab.tsx` to avoid colliding with
// `utils/economyFlow.ts`'s equivalents (Phase 3 Decision 6) — genuinely different row shapes
// (Count/Pts/Total + `multiplier`/`pv` vs. Calculation/Amount), not an unjustified duplicate.

export type MilitarySourceType = 'base' | 'building' | 'spell' | 'honor' | 'monarch' | 'general' | 'personality';

export const MILITARY_SOURCE_COLORS: Record<MilitarySourceType, string> = {
  base: '#6a7a8a',
  building: '#d4b85f',
  spell: '#9a7ab8',
  honor: '#c4a44a',
  monarch: '#c45a4a',
  general: '#6a9e8e',
  personality: '#b8724a',
};

export interface MilitaryFlowRow {
  label: string;
  count?: number;
  multiplier?: string;
  /** SF-180: `null` only for the final Modified Offense/Defense row when the source DTO field is
   *  absent (pre-deploy Redis cache) — renderers must show "—", never coerce to 0. */
  value: number | null;
  unit: string;
  source: MilitarySourceType;
  isSubtotal?: boolean;
  isTotal?: boolean;
  indent?: boolean;
  dimmed?: boolean;
}

function titleCase(value: string): string {
  return value.charAt(0) + value.slice(1).toLowerCase();
}

export function buildOffenseRows(p: Province): { rows: MilitaryFlowRow[]; raw: number } {
  const race = p.metadata.race;
  const stats = RACE_UNIT_STATS[race] || RACE_UNIT_STATS.HUMAN;
  const units = RACE_UNITS[race] || RACE_UNITS.HUMAN;

  const unitRows: { label: string; count: number; pv: number; points: number; dimmed: boolean }[] = [
    { label: 'Soldiers', count: p.population.soldiers, pv: stats.soldier.off, points: p.population.soldiers * stats.soldier.off, dimmed: false },
    { label: units.offense, count: p.population.offenseSpecialists, pv: stats.offense.off, points: p.population.offenseSpecialists * stats.offense.off, dimmed: false },
    { label: units.elite, count: p.population.elites, pv: stats.elite.off, points: p.population.elites * stats.elite.off, dimmed: false },
    { label: 'War Horses', count: p.population.warHorses, pv: 1, points: p.population.warHorses, dimmed: false },
    { label: 'Prisoners', count: p.population.prisoners, pv: 3, points: p.population.prisoners * 3, dimmed: false },
    { label: units.defense, count: p.population.defenseSpecialists, pv: stats.defense.off, points: p.population.defenseSpecialists * stats.defense.off, dimmed: true },
  ];

  const raw = unitRows.filter((r) => !r.dimmed).reduce((s, r) => s + r.points, 0);

  const rows: MilitaryFlowRow[] = unitRows.map((u) => ({
    label: u.label,
    count: u.count,
    multiplier: `×${u.pv}`,
    value: u.dimmed ? 0 : u.points,
    unit: 'pts',
    source: 'base' as MilitarySourceType,
    dimmed: u.dimmed || u.count === 0,
  }));

  rows.push({ label: 'Raw Offense', value: raw, unit: 'pts', source: 'base', isSubtotal: true });

  const ome = p.military.offensiveME;
  rows.push({ label: 'Offensive ME (OME)', value: 0, unit: pct(ome), source: 'building', indent: true });

  const hasFanaticism = p.magic.activeEffects.some((e) => e.effectType === 'FANATICISM');
  if (hasFanaticism) {
    rows.push({ label: 'Fanaticism', value: 0, unit: '+5%', source: 'spell', indent: true });
  }

  const honourMod = HONOUR_INCOME_MOD[p.metadata.title] || 1.0;
  if (honourMod !== 1.0) {
    rows.push({ label: `Honor (${titleCase(p.metadata.title)})`, value: 0, unit: `+${((honourMod - 1) * 100).toFixed(0)}%`, source: 'honor', indent: true });
  }

  const generalMod = p.population.generals > 0 ? 0.03 * (p.population.generals - 1) : 0;
  if (generalMod > 0) {
    rows.push({ label: `Generals (${p.population.generals})`, value: 0, unit: `+${(generalMod * 100).toFixed(0)}%`, source: 'general', indent: true });
  }

  // SF-180 Decision 2/5: Warrior personality's offense modifier is unconditional +15% in the
  // engine (Property.MILITARY_OFFENSE_MOD has no war-gating) — not war-gated in this waterfall row.
  if (p.metadata.personality === 'WARRIOR') {
    rows.push({ label: 'Warrior', value: 0, unit: '+15%', source: 'personality', indent: true });
  }

  rows.push({
    label: 'Modified Offense',
    value: typeof p.military.modifiedOffensePoints === 'number' ? p.military.modifiedOffensePoints : null,
    unit: 'pts',
    source: 'base',
    isTotal: true,
  });

  return { rows, raw };
}

export function buildDefenseRows(p: Province): { rows: MilitaryFlowRow[]; raw: number } {
  const race = p.metadata.race;
  const stats = RACE_UNIT_STATS[race] || RACE_UNIT_STATS.HUMAN;
  const units = RACE_UNITS[race] || RACE_UNITS.HUMAN;
  const effects = new Set(p.magic.activeEffects.map((e) => e.effectType));

  // SF-180 Decision 2: reuses homeDefense.ts's existing Aggression pattern verbatim rather than
  // reimplementing it a third time.
  const hasAggression = effects.has('AGGRESSION');
  const soldierDefensePv = stats.soldier.def - (hasAggression ? 1 : 0);

  const unitRows: { label: string; count: number; pv: number; points: number; dimmed: boolean }[] = [
    { label: 'Soldiers', count: p.population.soldiers, pv: soldierDefensePv, points: p.population.soldiers * soldierDefensePv, dimmed: false },
    { label: units.defense, count: p.population.defenseSpecialists, pv: stats.defense.def, points: p.population.defenseSpecialists * stats.defense.def, dimmed: false },
    { label: units.elite, count: p.population.elites, pv: stats.elite.def, points: p.population.elites * stats.elite.def, dimmed: false },
    { label: units.offense, count: p.population.offenseSpecialists, pv: stats.offense.def, points: 0, dimmed: true },
    { label: 'War Horses', count: p.population.warHorses, pv: 0, points: 0, dimmed: true },
    { label: 'Prisoners', count: p.population.prisoners, pv: 0, points: 0, dimmed: true },
  ];

  // SF-180 Decision 2: Townwatch contributes raw points (peasants/4) BEFORE DME/the land floor —
  // added as a real unit-like row (folded into `raw`), not a %-only annotation row, or the client's
  // "raw subtotal + modifier rows" would no longer sum to the server's modifiedDefensePoints.
  const hasTownwatch = effects.has('TOWNWATCH');
  if (hasTownwatch) {
    const townwatchPoints = Math.round(p.population.peasants / 4);
    unitRows.push({ label: 'Townwatch', count: p.population.peasants, pv: 0.25, points: townwatchPoints, dimmed: false });
  }

  const raw = unitRows.filter((r) => !r.dimmed).reduce((s, r) => s + r.points, 0);

  const rows: MilitaryFlowRow[] = unitRows.map((u) => ({
    label: u.label,
    count: u.count,
    multiplier: `×${u.pv}`,
    value: u.dimmed ? 0 : u.points,
    unit: 'pts',
    source: 'base' as MilitarySourceType,
    dimmed: u.dimmed || u.count === 0,
  }));

  rows.push({ label: 'Raw Defense', value: raw, unit: 'pts', source: 'base', isSubtotal: true });

  const dme = p.military.defensiveME;
  rows.push({ label: 'Defensive ME (DME)', value: 0, unit: pct(dme), source: 'building', indent: true });

  if (effects.has('MINOR_PROTECTION')) rows.push({ label: 'Minor Protection', value: 0, unit: '+5%', source: 'spell', indent: true });
  if (effects.has('GREATER_PROTECTION')) rows.push({ label: 'Greater Protection', value: 0, unit: '+5%', source: 'spell', indent: true });
  if (effects.has('FANATICISM')) rows.push({ label: 'Fanaticism', value: 0, unit: '-3%', source: 'spell', indent: true });
  if (effects.has('PLAGUE')) rows.push({ label: 'Plague', value: 0, unit: '-15%', source: 'spell', indent: true });

  // SF-180 Decision 1: re-gated to also require hasHostileOrWarRelationship — matches
  // MilitarySystem.computeDefense()'s real monarch-bonus gate (SF-82c) instead of isMonarch alone.
  if (p.metadata.isMonarch && p.kingdom.hasHostileOrWarRelationship === true) {
    rows.push({ label: 'Monarch bonus', value: 0, unit: '+10%', source: 'monarch', indent: true });
  }

  rows.push({
    label: 'Modified Defense',
    value: typeof p.military.modifiedDefensePoints === 'number' ? p.military.modifiedDefensePoints : null,
    unit: 'pts',
    source: 'base',
    isTotal: true,
  });

  return { rows, raw };
}

// ─── Active Modifiers ────────────────────────────────────────────────────────
// Phase 3 Decision 6: `offense`/`defense`/`other` are structured `ModifierValue | null` (reused
// from `utils/economyFlow.ts`), not the original `string | null` — `negative` is set explicitly at
// each push site, fixing the `value.startsWith('-')` string-sniffing bug (e.g. Inspire Army's
// "-15% wages" is a benefit, not a loss).

export interface MilitaryModifier {
  name: string;
  source: MilitarySourceType;
  offense: ModifierValue | null;
  defense: ModifierValue | null;
  other: ModifierValue | null;
}

export function buildModifiers(p: Province): MilitaryModifier[] {
  const mods: MilitaryModifier[] = [];
  const effects = new Set(p.magic.activeEffects.map((e) => e.effectType));
  const tg = p.territory.buildings.TRAINING_GROUND || 0;
  const forts = p.territory.buildings.FORT || 0;

  if (tg > 0) {
    mods.push({ name: `Training Grounds (${tg})`, source: 'building', offense: { text: 'Included in OME', negative: false }, defense: null, other: null });
  }
  if (forts > 0) {
    mods.push({ name: `Forts (${forts})`, source: 'building', offense: null, defense: { text: 'Included in DME', negative: false }, other: null });
  }

  const honourMod = HONOUR_INCOME_MOD[p.metadata.title] || 1.0;
  if (honourMod !== 1.0) {
    mods.push({
      name: `Honor (${titleCase(p.metadata.title)})`,
      source: 'honor',
      offense: { text: `+${((honourMod - 1) * 100).toFixed(0)}%`, negative: false },
      defense: null,
      other: null,
    });
  }

  if (p.population.generals > 1) {
    mods.push({
      name: `Generals (${p.population.generals})`,
      source: 'general',
      offense: { text: `+${(p.population.generals - 1) * 3}%`, negative: false },
      defense: null,
      other: null,
    });
  }

  if (p.metadata.isMonarch) {
    mods.push({ name: 'Monarch', source: 'monarch', offense: null, defense: { text: '+10%', negative: false }, other: null });
  }

  if (effects.has('FANATICISM')) {
    mods.push({ name: 'Fanaticism', source: 'spell', offense: { text: '+5%', negative: false }, defense: { text: '-3%', negative: true }, other: null });
  }
  if (effects.has('MINOR_PROTECTION')) {
    mods.push({ name: 'Minor Protection', source: 'spell', offense: null, defense: { text: '+5%', negative: false }, other: null });
  }
  if (effects.has('GREATER_PROTECTION')) {
    mods.push({ name: 'Greater Protection', source: 'spell', offense: null, defense: { text: '+5%', negative: false }, other: null });
  }
  if (effects.has('PLAGUE')) {
    mods.push({ name: 'Plague', source: 'spell', offense: null, defense: { text: '-15%', negative: true }, other: { text: '-15% income', negative: true } });
  }
  if (effects.has('INSPIRE_ARMY')) {
    // Both benefits (lower wages, faster training) despite the leading '-' sign — a sign-sniffing
    // classifier would have gotten this backwards (the exact bug this spec fixes).
    mods.push({ name: 'Inspire Army', source: 'spell', offense: null, defense: null, other: { text: '-15% wages, -20% train time', negative: false } });
  }
  if (effects.has('AGGRESSION')) {
    // SF-180 Decision 10: no Aggression *offense* effect exists anywhere in the engine (repo-wide
    // grep) — the old offense: 'Active' cell was fabricated. The real effect is a defense penalty:
    // UnitSystem.java decrements the soldier's per-race base defense point by a flat -1 (Human
    // 1→0, Halfling 2→1, ...) before multiplying by soldier count — the decrement itself is always
    // exactly -1 regardless of race, so this text is race-invariant even though the resulting
    // per-unit value isn't.
    mods.push({ name: 'Aggression', source: 'spell', offense: null, defense: { text: '-1 def/soldier', negative: true }, other: null });
  }

  return mods;
}

// ─── Training planner (Component 1c) ────────────────────────────────────────

const UNIT_TYPE_ORDER = ['OFFENSE_SPECIALIST', 'DEFENSE_SPECIALIST', 'ELITE', 'THIEF'];

export function unitTypeLabel(unitType: string, race: string): string {
  switch (unitType) {
    case 'OFFENSE_SPECIALIST': return getRaceUnitName(race, 'offense');
    case 'DEFENSE_SPECIALIST': return getRaceUnitName(race, 'defense');
    case 'ELITE': return getRaceUnitName(race, 'elite');
    case 'THIEF': return 'Thieves';
    default: return unitType;
  }
}

export interface TrainingPlannerData {
  days: number[];
  units: { unitType: string; label: string; byDay: Record<number, number> }[];
  totals: Record<number, number>;
}

/** `TroopsTab.tsx`'s `buildTrainingPlannerData`, ported unchanged (Phase 3 correction — it already
 *  groups raw per-tick batches by unitType + day before rendering; the SF-140/SF-170 "aggregate
 *  across days" lesson doesn't apply since one-column-per-day is deliberate here). */
export function buildTrainingPlannerData(training: Training[], race: string): TrainingPlannerData | null {
  if (training.length === 0) return null;

  const map = new Map<string, Record<number, number>>();
  for (const t of training) {
    const entry = map.get(t.unitType) ?? {};
    entry[t.ticksRemaining] = (entry[t.ticksRemaining] ?? 0) + t.unitCount;
    map.set(t.unitType, entry);
  }

  const days = Array.from({ length: 24 }, (_, i) => i + 1);

  const orderedTypes = UNIT_TYPE_ORDER.filter((ut) => map.has(ut));
  for (const ut of map.keys()) {
    if (!orderedTypes.includes(ut)) orderedTypes.push(ut);
  }

  const units = orderedTypes.map((ut) => ({
    unitType: ut,
    label: unitTypeLabel(ut, race),
    byDay: map.get(ut)!,
  }));

  const totals: Record<number, number> = {};
  for (const day of days) {
    totals[day] = training.filter((t) => t.ticksRemaining === day).reduce((sum, t) => sum + t.unitCount, 0);
  }

  return { days, units, totals };
}
